import { useState } from 'react';

interface ProductImage {
  url: string;
  altText: string;
}

interface ImageGalleryProps {
  images: ProductImage[];
  title: string;
}

export default function ImageGallery({ images, title }: ImageGalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  if (!images.length) return null;

  const prev = () => setActiveIndex((i) => (i - 1 + images.length) % images.length);
  const next = () => setActiveIndex((i) => (i + 1) % images.length);

  return (
    <div className="flex flex-col gap-4">
      {/* Main Image */}
      <div className="relative w-full aspect-[4/5] bg-[#111] rounded-2xl overflow-hidden group">
        <img
          src={images[activeIndex].url}
          alt={images[activeIndex].altText || title}
          className="w-full h-full object-cover object-top transition-opacity duration-500"
        />
        {/* Cyan glow overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none"></div>

        {images.length > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-black/60 border border-[#00d4ff]/30 text-[#00d4ff] hover:bg-[#00d4ff]/20 transition-all duration-300 cursor-pointer opacity-0 group-hover:opacity-100 whitespace-nowrap"
              style={{ boxShadow: '0 0 12px rgba(0,212,255,0.2)' }}
              aria-label="Previous image"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <button
              onClick={next}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-black/60 border border-[#00d4ff]/30 text-[#00d4ff] hover:bg-[#00d4ff]/20 transition-all duration-300 cursor-pointer opacity-0 group-hover:opacity-100 whitespace-nowrap"
              style={{ boxShadow: '0 0 12px rgba(0,212,255,0.2)' }}
              aria-label="Next image"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
            {/* Dot indicators */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className={`w-1.5 h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                    i === activeIndex ? 'bg-[#00d4ff] w-4' : 'bg-white/40'
                  }`}
                  aria-label={`Image ${i + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="flex gap-3 overflow-x-auto pb-1">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setActiveIndex(i)}
              className={`flex-shrink-0 w-20 h-24 rounded-lg overflow-hidden border-2 transition-all duration-300 cursor-pointer ${
                i === activeIndex
                  ? 'border-[#00d4ff] shadow-[0_0_12px_rgba(0,212,255,0.5)]'
                  : 'border-transparent opacity-50 hover:opacity-80'
              }`}
            >
              <img
                src={img.url}
                alt={img.altText || title}
                className="w-full h-full object-cover object-top"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
